/**
 * Mac Desktop Launcher Component
 * Allows users to launch new macOS EC2 desktops with NICE DCV
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Card, Button, Select, Typography, Space, message, Spin, Radio } from "antd";
import { AppleFilled, RocketOutlined, DesktopOutlined } from "@ant-design/icons";
import { launchMacDesktop } from "./store/macSlice";
import "./MacDesktopLauncher.scss";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const INSTANCE_TYPES = [
  { value: "mac2.metal", label: "mac2.metal - Apple M2, 8 vCPU, 16GB RAM (Recommended)", recommended: true },
  { value: "mac2-m2pro.metal", label: "mac2-m2pro.metal - Apple M2 Pro, 12 vCPU, 32GB RAM", recommended: false },
  { value: "mac-m4.metal", label: "mac-m4.metal - Apple M4, 10 vCPU, 24GB RAM", recommended: false }
];

function MacDesktopLauncher() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [instanceType, setInstanceType] = useState("mac2.metal");
  const [persistent, setPersistent] = useState(true);
  const [isLaunching, setIsLaunching] = useState(false);

  const handleLaunch = async () => {
    setIsLaunching(true);

    try {
      const result = await dispatch(
        launchMacDesktop({
          template: "macos-dcv",
          instanceType: instanceType,
          volumeSize: 200,
          persistent: persistent
        })
      ).unwrap();

      message.success("macOS desktop is being provisioned...");

      // Navigate to the viewer
      navigate(`/control/macos/${result.id}`);
    } catch (error) {
      console.error("Failed to create macOS desktop:", error);
      message.error(error?.message || "Failed to launch macOS desktop");
      setIsLaunching(false);
    }
  };

  return (
    <div className="mac-launcher-container">
      <Card className="launcher-card">
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {/* Header */}
          <div className="launcher-header">
            <AppleFilled className="mac-icon" />
            <div>
              <Title level={3} style={{ margin: 0 }}>
                macOS Desktop
              </Title>
              <Text type="secondary">Launch a macOS EC2 instance with NICE DCV</Text>
            </div>
          </div>

          {/* Description */}
          <Paragraph>
            Launch a powerful macOS desktop in the cloud powered by Apple Silicon. Your desktop will be
            accessible through NICE DCV for high-performance remote access.
          </Paragraph>

          {/* Instance Type Selection */}
          <div className="instance-selection">
            <Text strong>Instance Type</Text>
            <Select
              value={instanceType}
              onChange={setInstanceType}
              style={{ width: "100%" }}
              size="large"
              disabled={isLaunching}
            >
              {INSTANCE_TYPES.map((type) => (
                <Option key={type.value} value={type.value}>
                  <Space>
                    <DesktopOutlined />
                    {type.label}
                  </Space>
                </Option>
              ))}
            </Select>
          </div>

          {/* Desktop Mode */}
          <div className="mode-selection">
            <Text strong>Desktop Mode</Text>
            <Radio.Group
              value={persistent}
              onChange={(e) => setPersistent(e.target.value)}
              disabled={isLaunching}
              style={{ width: "100%", marginTop: 8 }}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value={true} style={{ width: "50%", textAlign: "center" }}>
                Persistent
              </Radio.Button>
              <Radio.Button value={false} style={{ width: "50%", textAlign: "center" }}>
                Ephemeral
              </Radio.Button>
            </Radio.Group>
            <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
              {persistent
                ? "You can pause your desktop and resume later. Storage is preserved."
                : "Desktop runs until you exit. Termination destroys all data."}
            </Text>
          </div>

          {/* Features */}
          <div className="features-grid">
            <div className="feature-item">
              <Text strong>Apple Silicon</Text>
              <Text type="secondary">M2 or M2 Pro</Text>
            </div>
            <div className="feature-item">
              <Text strong>Storage</Text>
              <Text type="secondary">200 GB SSD</Text>
            </div>
            <div className="feature-item">
              <Text strong>Remote Access</Text>
              <Text type="secondary">NICE DCV</Text>
            </div>
            <div className="feature-item">
              <Text strong>OS</Text>
              <Text type="secondary">macOS Sonoma</Text>
            </div>
          </div>

          {/* Launch Button */}
          <Button
            type="primary"
            size="large"
            icon={isLaunching ? <Spin size="small" /> : <RocketOutlined />}
            onClick={handleLaunch}
            disabled={isLaunching}
            block
          >
            {isLaunching ? "Launching..." : "Launch macOS Desktop"}
          </Button>

          {isLaunching && (
            <Text type="secondary" style={{ textAlign: "center", display: "block" }}>
              This may take 2-3 minutes. You will be redirected automatically.
            </Text>
          )}
        </Space>
      </Card>
    </div>
  );
}

export default MacDesktopLauncher;
